import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { useSidebar } from '../../context/SidebarContext.jsx';
import {
  X,
  Home,
  BookOpen,
  Radio,
  TrendingUp,
  Bot,
  Settings,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronRight,
  User,
  FileCode,
  Menu,
  MessageSquare,
  LifeBuoy,
  Shield,
  Image,
  Bell
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { isOpen, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const [myCoursesOpen, setMyCoursesOpen] = useState(false);
  const [sistemOpen, setSistemOpen] = useState(false); // NEW: SISTEM dropdown state
  const [tradingviewOpen, setTradingviewOpen] = useState(false); // NEW: Tradingview dropdown state
  const [ebooksOpen, setEbooksOpen] = useState(false);


  // Auto-close sidebar on navigation for ALL devices
  useEffect(() => {
    if (isOpen) {
      toggleSidebar();
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin/dashboard';
    if (user.role === 'instructor') return '/instructor/dashboard';
    return '/student/dashboard';
  };

  const getLiveStreamingLink = () => {
    if (!user) return '/live-streaming';
    if (user.role === 'admin') return '/admin/webinars';
    return '/live-streaming';
  };

  // Don't show sidebar on login/register pages or if user is not authenticated
  if (!user || location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Floating Menu Button - When sidebar is closed - FOR OPEN */}
      {!isOpen && (
        <button
          className="fixed top-4 left-4 z-[9999] bg-[white] dark:!bg-black p-3 rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-200 border border-gray-200 dark:border-none"
          onClick={toggleSidebar}
          aria-label="Open Sidebar"
        >
          <Menu size={24} className="text-gray-800 dark:text-white" />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-[white] dark:!bg-black text-gray-800 dark:text-white flex flex-col z-[10000] transition-all duration-300 border-r border-gray-200 dark:border-r-0 overflow-hidden
          ${isOpen ? 'w-64' : 'w-0'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Close Button (X) - FOR ALL DEVICES - FOR CLOSE */}
        {isOpen && (
          <button
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors z-[10001]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSidebar();
            }}
            aria-label="Close Sidebar"
          >
            <X size={20} className="text-gray-500 dark:text-white" />
          </button>
        )}

        {/* Header Section */}
        <div className="pt-14 pb-4 px-6 border-b border-gray-200 dark:border-white/10">
          <h1 className="text-base font-bold text-gray-900 dark:text-white mb-1">
            Learn 2 Trade
          </h1>
          <p className="text-xs text-gray-500 dark:text-white/60">
            Learn. Trade. Succeed.
          </p>
        </div>

        {/* User Profile Card */}
        <div className="p-6 border-b border-gray-200 dark:border-white/10">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-[#30C4B5] rounded-full flex items-center justify-center mr-3 text-white">
              <span className="font-semibold text-lg">
                {user?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 dark:text-white/60 truncate">
                {user?.email}
              </p>
              <span className="inline-block mt-1 px-2 py-0.5 text-[10px] bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/80 rounded border border-gray-200 dark:border-transparent">
                {user?.role}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {/* Dashboard */}
          <Link
            to={getDashboardLink()}
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive(getDashboardLink())
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <Home size={20} className="mr-3" />
            <span className="text-sm font-medium">Dashboard</span>
          </Link>

          {/* My Courses - Only for students */}
          {user?.role === 'student' && (
            <>
              <div
                className={`
                  flex items-center px-4 py-3 mb-1 rounded-lg cursor-pointer transition-all duration-200
                  ${location.pathname.startsWith('/courses') || location.pathname.startsWith('/my-courses')
                    ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                    : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
                onClick={() => {
                  setMyCoursesOpen(!myCoursesOpen);
                  navigate('/my-courses');
                }}
              >
                <BookOpen size={20} className="mr-3" />
                <span className="flex-1 text-sm font-medium">My Courses</span>
                {myCoursesOpen ? (
                  <ChevronDown size={16} className={`text-current opacity-70`} />
                ) : (
                  <ChevronRight size={16} className={`text-current opacity-70`} />
                )}
              </div>

              {/* My Courses Submenu */}
              {myCoursesOpen && (
                <div className="ml-4 mb-2 space-y-1 border-l border-gray-200 dark:border-white/10 pl-2">
                  <Link
                    to="/courses"
                    className={`
                      flex items-center px-4 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/courses')
                        ? 'bg-[#30C4B5]/10 dark:bg-yellow-500/10 text-[#30C4B5] dark:text-yellow-500 font-semibold'
                        : 'text-gray-500 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
                      }
                    `}
                  >
                    All My Courses
                  </Link>

                  {/* SISTEM Nested Submenu */}
                  <div
                    className="flex items-center px-4 py-2 rounded-lg cursor-pointer text-sm text-gray-500 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
                    onClick={() => setSistemOpen(!sistemOpen)}
                  >
                    <span className="flex-1">🎯 SISTEM</span>
                    {sistemOpen ? (
                      <ChevronDown size={14} className="opacity-70" />
                    ) : (
                      <ChevronRight size={14} className="opacity-70" />
                    )}
                  </div>

                  {sistemOpen && (
                    <div className="ml-4 space-y-1">
                      <Link
                        to="/sistem/indicator"
                        className="block px-4 py-1.5 text-xs text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 rounded-lg transition-all duration-200"
                        onClick={toggleSidebar}
                      >
                        Indicator
                      </Link>
                      <Link
                        to="/sistem/ea-copier"
                        className="block px-4 py-1.5 text-xs text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 rounded-lg transition-all duration-200"
                        onClick={toggleSidebar}
                      >
                        EA Copier
                      </Link>
                    </div>
                  )}

                  {/* Tradingview Nested Submenu */}
                  <div
                    className="flex items-center px-4 py-2 rounded-lg cursor-pointer text-sm text-gray-500 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
                    onClick={() => setTradingviewOpen(!tradingviewOpen)}
                  >
                    <span className="flex-1">📈 Tradingview</span>
                    {tradingviewOpen ? (
                      <ChevronDown size={14} className="opacity-70" />
                    ) : (
                      <ChevronRight size={14} className="opacity-70" />
                    )}
                  </div>

                  {tradingviewOpen && (
                    <div className="ml-4 space-y-1">
                      <Link
                        to="/tradingview/laptop"
                        className="block px-4 py-1.5 text-xs text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 rounded-lg transition-all duration-200"
                        onClick={toggleSidebar}
                      >
                        Modul Laptop
                      </Link>
                      <Link
                        to="/tradingview/mobile"
                        className="block px-4 py-1.5 text-xs text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 rounded-lg transition-all duration-200"
                        onClick={toggleSidebar}
                      >
                        Modul Handphone
                      </Link>
                    </div>
                  )}

                  {/* FCPO Link */}
                  <Link
                    to="/tradingview/fcpo"
                    className="flex items-center px-4 py-2 rounded-lg text-sm text-gray-500 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
                    onClick={toggleSidebar}
                  >
                    <span className="flex-1">FCPO</span>
                  </Link>

                  {/* Ebooks Nested Submenu */}
                  <div
                    className="flex items-center px-4 py-2 rounded-lg cursor-pointer text-sm text-gray-500 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
                    onClick={() => setEbooksOpen(!ebooksOpen)}
                  >
                    <span className="flex-1">Ebooks</span>
                    {ebooksOpen ? (
                      <ChevronDown size={14} className="opacity-70" />
                    ) : (
                      <ChevronRight size={14} className="opacity-70" />
                    )}
                  </div>

                  {ebooksOpen && (
                    <div className="ml-4 space-y-1">
                      <Link
                        to="/courses?courseType=ebook&ebookSubcategory=basic"
                        className="block px-4 py-1.5 text-xs text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 rounded-lg transition-all duration-200"
                        onClick={toggleSidebar}
                      >
                        Basic
                      </Link>
                      <Link
                        to="/courses?courseType=ebook&ebookSubcategory=intermediate"
                        className="block px-4 py-1.5 text-xs text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 rounded-lg transition-all duration-200"
                        onClick={toggleSidebar}
                      >
                        Intermediate
                      </Link>
                      <Link
                        to="/courses?courseType=ebook&ebookSubcategory=advanced"
                        className="block px-4 py-1.5 text-xs text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 rounded-lg transition-all duration-200"
                        onClick={toggleSidebar}
                      >
                        Advanced
                      </Link>
                    </div>
                  )}

                  {/* Video Courses Nested Submenu REMOVED */}
                </div>
              )}
            </>
          )}

          {/* Instructor Dashboard - For instructors and admins */}
          {(user?.role === 'instructor' || user?.role === 'admin') && (
            <Link
              to="/instructor/dashboard"
              className={`
                flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
                ${isActive('/instructor/dashboard')
                  ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                  : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
                }
              `}
            >
              <BookOpen size={20} className="mr-3" />
              <span className="text-sm font-medium">Instructor</span>
            </Link>
          )}

          {/* Admin Dashboard - For admins only */}
          {user?.role === 'admin' && (
            <>
              <Link
                to="/admin/dashboard"
                className={`
                  flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
                  ${isActive('/admin/dashboard')
                    ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                    : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
              >
                <Settings size={20} className="mr-3" />
                <span className="text-sm font-medium">Admin</span>
              </Link>
              <Link
                to="/admin/users"
                className={`
                  flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
                  ${isActive('/admin/users')
                    ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                    : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
              >
                <User size={20} className="mr-3" />
                <span className="text-sm font-medium">User Management</span>
              </Link>
            </>
          )}

          {/* Live Streaming / Webinars */}
          <Link
            to={getLiveStreamingLink()}
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/live-streaming') || isActive('/webinars') || isActive('/admin/webinars')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <Radio size={20} className="mr-3" />
            <span className="text-sm font-medium">Live Streaming</span>
          </Link>

          {/* Community Hub */}
          <Link
            to="/community"
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/community') || isActive('/chat') || isActive('/admin-support') || isActive('/knowledge-base')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <MessageSquare size={20} className="mr-3" />
            <span className="text-sm font-medium">Community</span>
          </Link>

          {/* Alert Tradingview / Indicator Alert */}
          <Link
            to={user?.role === 'admin' ? "/admin/indicator-alerts" : "/tradingview-alerts"}
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/tradingview-alerts') || isActive('/admin/indicator-alerts')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <Bell size={20} className="mr-3" />
            <span className="text-sm font-medium">Alert Tradingview</span>
          </Link>

          {/* Student Settings REMOVED (Duplicate) */}

          {user?.role === 'admin' && (
            <Link
              to="/admin-panel"
              className={`
                flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
                ${isActive('/admin-panel')
                  ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                  : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
                }
              `}
            >
              <Shield size={20} className="mr-3" />
              <span className="text-sm font-medium">Admin Panel</span>
            </Link>
          )}

          {/* AI Trading Module */}
          <Link
            to="/ai-trading"
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/ai-trading')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <TrendingUp size={20} className="mr-3" />
                <span className="text-sm font-medium">AI Trading Module</span>
              </div>
              <span className="bg-yellow-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded">BETA</span>
            </div>
          </Link>

          {/* The Coder AI */}
          <Link
            to="/coder-ai"
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/coder-ai')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <Bot size={20} className="mr-3" />
            <span className="text-sm font-medium">The Coder AI</span>
          </Link>

          {/* Code Templates */}
          <Link
            to="/coder-ai/templates"
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/coder-ai/templates')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <FileCode size={20} className="mr-3" />
            <span className="text-sm font-medium">Code Templates</span>
          </Link>


        </nav>

        {/* Bottom Section */}
        <div className="p-4 border-t border-gray-200 dark:border-white/10">
          <Link
            to="/settings"
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/settings')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <Settings size={20} className="mr-3" />
            <span className="text-sm font-medium">Settings</span>
          </Link>

          <Link
            to="/help"
            className={`
              flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200
              ${isActive('/help')
                ? 'bg-[#30C4B5] dark:bg-yellow-500 text-white dark:text-black shadow-md shadow-[#30C4B5]/20 dark:shadow-yellow-500/20'
                : 'text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
          >
            <HelpCircle size={20} className="mr-3" />
            <span className="text-sm font-medium">Help Center</span>
          </Link>

          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 rounded-lg text-gray-600 dark:text-white/80 hover:bg-red-50 hover:text-red-600 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-400 dark:hover:text-white transition-all duration-200"
          >
            <LogOut size={20} className="mr-3" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Spacer for desktop - REMOVED! No more spacer */}
    </>
  );
};

export default Sidebar;